/**
 * WeaveIn.ai Platform - Signature Center
 * Unified view: Pending approvals, sent requests, received signatures, tracking.
 * Covers both internal approvals and external client/partner signature tracking.
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  PenTool, Send, CheckCircle, Clock, AlertTriangle, Mail, Smartphone,
  Link2, User, Building2, RefreshCw, Download, Eye, Plus, Filter,
  ExternalLink, Inbox, ArrowUpRight, XCircle,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { SignaturePad, SignatureResult } from './SignaturePad';
import { SignatureViewer } from './SignatureViewer';
import { EnvelopeViewer } from './EnvelopeViewer';
import { onOpenRecord, takePendingRecord } from '@/lib/recordDeepLinks';
import { RequestSignaturePage } from './RequestSignaturePage';
import { config } from '@/src/config';

const getAuthHeaders = () => {
  const token = localStorage.getItem('auth_token');
  const cboId = localStorage.getItem('active_cbo_id');
  return {
    'Content-Type': 'application/json',
    ...(token && { Authorization: `Bearer ${token}` }),
    ...(cboId && { 'X-CBO-ID': cboId }),
  };
};

const statusColors: Record<string, string> = {
  pending: 'bg-yellow-100 text-yellow-800',
  sent: 'bg-blue-100 text-blue-800',
  viewed: 'bg-indigo-100 text-indigo-800',
  signed: 'bg-green-100 text-green-800',
  declined: 'bg-red-100 text-red-800',
  expired: 'bg-gray-100 text-gray-800',
  cancelled: 'bg-gray-100 text-gray-500',
};

const statusIcons: Record<string, React.ReactNode> = {
  pending: <Clock className="w-3.5 h-3.5" />,
  sent: <Send className="w-3.5 h-3.5" />,
  viewed: <Eye className="w-3.5 h-3.5" />,
  signed: <CheckCircle className="w-3.5 h-3.5" />,
  declined: <XCircle className="w-3.5 h-3.5" />,
  expired: <AlertTriangle className="w-3.5 h-3.5" />,
};

export function SignatureCenter() {
  const [activeTab, setActiveTab] = useState('inbox');
  const [requests, setRequests] = useState<any[]>([]);
  const [pendingApprovals, setPendingApprovals] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [showNewRequestPage, setShowNewRequestPage] = useState(false);
  const [selectedRequestId, setSelectedRequestId] = useState<number | null>(null);
  // TRUS-685: /approvals?request=<id> (a system-event link) opens that
  // request under "All", expanded — or on its own if it is not in this list.
  const [linkedRequestId, setLinkedRequestId] = useState<number | null>(null);
  useEffect(() => {
    const open = (requestId: number) => {
      setActiveTab('all');
      setSelectedRequestId(requestId);
      setLinkedRequestId(requestId);
    };
    const parked = takePendingRecord('signature');
    if (parked) open(parked.requestId);
    return onOpenRecord('signature', (link) => {
      open(link.requestId);
      return true;
    });
  }, []);

  // Signature dialog for pending approvals
  const [sigDialogOpen, setSigDialogOpen] = useState(false);
  const [signingItem, setSigningItem] = useState<any>(null);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      // Fetch all signing requests
      const reqRes = await fetch(`${config.weaveinApi.baseUrl}/api/signatures/requests`, {
        credentials: 'include', headers: getAuthHeaders(),
      });
      if (reqRes.ok) setRequests(await reqRes.json());

      // Fetch pending approvals
      const appRes = await fetch(`${config.weaveinApi.baseUrl}/api/signatures/pending-approvals`, {
        credentials: 'include', headers: getAuthHeaders(),
      });
      if (appRes.ok) {
        const data = await appRes.json();
        setPendingApprovals([...(data.signing_requests || []), ...(data.approval_steps || [])]);
      }
    } catch (e) {
      console.error('Error fetching signatures:', e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const pendingCount = requests.filter(r => ['pending', 'sent', 'viewed'].includes(r.status)).length;
  const signedCount = requests.filter(r => r.status === 'signed').length;
  const sentCount = requests.filter(r => ['sent', 'viewed'].includes(r.status)).length;

  const handleApprovalSign = async (result: SignatureResult) => {
    if (!signingItem) return;
    try {
      if (signingItem.type === 'signature') {
        await fetch(`${config.weaveinApi.baseUrl}/api/signatures/requests/${signingItem.id}/sign`, {
          method: 'POST', credentials: 'include', headers: getAuthHeaders(),
          body: JSON.stringify(result),
        });
      } else {
        const reqRes = await fetch(`${config.weaveinApi.baseUrl}/api/signatures/requests`, {
          method: 'POST', credentials: 'include', headers: getAuthHeaders(),
          body: JSON.stringify({
            title: `Approval: ${signingItem.step_name || signingItem.title}`,
            workflow_step_instance_id: signingItem.id,
            signer_type: 'internal',
            signer_role: signingItem.step_config?.approver_role || 'supervisor',
            expires_in_hours: 168,
          }),
        });
        const sigReq = await reqRes.json();
        await fetch(`${config.weaveinApi.baseUrl}/api/signatures/requests/${sigReq.id}/sign`, {
          method: 'POST', credentials: 'include', headers: getAuthHeaders(),
          body: JSON.stringify(result),
        });
      }
      setSigDialogOpen(false);
      setSigningItem(null);
      setTimeout(fetchAll, 500);
    } catch (e: any) {
      alert(`Error: ${e.message}`);
    }
  };

  const openProof = (requestId: number) => {
    const url = `${config.weaveinApi.baseUrl}/api/signatures/requests/${requestId}/proof/html`;
    fetch(url, { headers: getAuthHeaders() })
      .then(r => r.text())
      .then(html => {
        const win = window.open('', '_blank');
        if (win) { win.document.write(html); win.document.close(); }
      });
  };

  const [resending, setResending] = useState<number | null>(null);

  const resendRequest = async (requestId: number, sendVia: string = 'email') => {
    setResending(requestId);
    try {
      const res = await fetch(`${config.weaveinApi.baseUrl}/api/signatures/requests/${requestId}/send`, {
        method: 'POST', credentials: 'include',
        headers: getAuthHeaders(),
        body: JSON.stringify({ send_via: sendVia }),
      });
      if (!res.ok) throw new Error('Send failed');
      const data = await res.json();
      if (data.delivered) {
        alert('Email resent successfully');
      } else {
        alert('Request processed but email delivery could not be confirmed');
      }
    } catch (e: any) {
      alert(`Resend failed: ${e.message}`);
    } finally {
      setResending(null);
    }
  };

  const renderRequest = (req: any) => (
    <Card key={req.id} className="mb-2">
      <CardContent className="py-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3 min-w-0">
            <div className="flex-shrink-0">
              {req.signer_type === 'external' ? (
                <Building2 className="w-5 h-5 text-purple-600" />
              ) : (
                <User className="w-5 h-5 text-blue-600" />
              )}
            </div>
            <div className="min-w-0">
              <div className="font-medium text-sm truncate">{req.title}</div>
              <div className="text-xs text-muted-foreground flex items-center gap-2 flex-wrap">
                <span>{req.signer_name || 'Unknown'}</span>
                {req.signer_email && <span className="truncate">{req.signer_email}</span>}
                {req.signer_role && <Badge variant="outline" className="text-xs">{req.signer_role}</Badge>}
                <Badge className={`text-xs ${statusColors[req.status] || ''}`}>
                  {statusIcons[req.status]} {req.status}
                </Badge>
              </div>
              <div className="text-xs text-muted-foreground mt-0.5">
                {req.created_at && `Created ${new Date(req.created_at).toLocaleDateString()}`}
                {req.expires_at && ` · Expires ${new Date(req.expires_at).toLocaleDateString()}`}
                {req.signed_at && ` · Signed ${new Date(req.signed_at).toLocaleString()}`}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-1 flex-shrink-0">
            <Button size="sm" variant="ghost" className="h-7 px-2"
              onClick={() => setSelectedRequestId(selectedRequestId === req.id ? null : req.id)}
              title="View details">
              <Eye className="w-3 h-3" />
            </Button>
            {req.status === 'signed' && (
              <Button size="sm" variant="ghost" className="h-7 px-2" onClick={() => openProof(req.id)}
                title="Download proof">
                <Download className="w-3 h-3" />
              </Button>
            )}
            {['pending', 'sent', 'viewed'].includes(req.status) && req.signer_email && (
              <Button size="sm" variant="ghost" className="h-7 px-2"
                disabled={resending === req.id}
                onClick={() => resendRequest(req.id, 'email')}
                title="Resend email">
                {resending === req.id ? <RefreshCw className="w-3 h-3 animate-spin" /> : <Mail className="w-3 h-3" />}
              </Button>
            )}
            {req.signing_url && ['pending', 'sent', 'viewed'].includes(req.status) && (
              <Button size="sm" variant="ghost" className="h-7 px-2" onClick={() => {
                navigator.clipboard.writeText(req.signing_url);
              }} title="Copy signing link">
                <Link2 className="w-3 h-3" />
              </Button>
            )}
          </div>
        </div>
        {selectedRequestId === req.id && (
          <div className="mt-3 border-t pt-3">
            <EnvelopeViewer requestId={req.id} />
          </div>
        )}
      </CardContent>
    </Card>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-32">
        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary" />
      </div>
    );
  }

  // Show full-page request flow
  if (showNewRequestPage) {
    return (
      <RequestSignaturePage
        onBack={() => { setShowNewRequestPage(false); setTimeout(fetchAll, 300); }}
      />
    );
  }

  return (
    <div className="space-y-4">
      {/* Toolbar */}
      <div className="flex flex-wrap items-center justify-end gap-2">
        <Button variant="ghost" size="sm" onClick={fetchAll}>
          <RefreshCw className="w-4 h-4" />
        </Button>
        <Button size="sm" onClick={() => setShowNewRequestPage(true)}>
          <Plus className="w-4 h-4 mr-1" /> New Request
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-4 gap-3">
        <Card><CardContent className="py-3 text-center">
          <div className="text-2xl font-bold text-amber-600">{pendingApprovals.length}</div>
          <div className="text-xs text-muted-foreground">My Pending</div>
        </CardContent></Card>
        <Card><CardContent className="py-3 text-center">
          <div className="text-2xl font-bold text-blue-600">{sentCount}</div>
          <div className="text-xs text-muted-foreground">Awaiting Signature</div>
        </CardContent></Card>
        <Card><CardContent className="py-3 text-center">
          <div className="text-2xl font-bold text-green-600">{signedCount}</div>
          <div className="text-xs text-muted-foreground">Signed</div>
        </CardContent></Card>
        <Card><CardContent className="py-3 text-center">
          <div className="text-2xl font-bold text-gray-600">{requests.length}</div>
          <div className="text-xs text-muted-foreground">Total</div>
        </CardContent></Card>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="inbox" className="flex items-center gap-1.5">
            <Inbox className="w-3.5 h-3.5" /> My Approvals
            {pendingApprovals.length > 0 && <Badge variant="destructive" className="text-xs h-4 px-1">{pendingApprovals.length}</Badge>}
          </TabsTrigger>
          <TabsTrigger value="sent" className="flex items-center gap-1.5">
            <Send className="w-3.5 h-3.5" /> Sent Requests
            {pendingCount > 0 && <Badge variant="secondary" className="text-xs h-4 px-1">{pendingCount}</Badge>}
          </TabsTrigger>
          <TabsTrigger value="signed" className="flex items-center gap-1.5">
            <CheckCircle className="w-3.5 h-3.5" /> Completed
          </TabsTrigger>
          <TabsTrigger value="all" className="flex items-center gap-1.5">
            <Filter className="w-3.5 h-3.5" /> All
          </TabsTrigger>
        </TabsList>

        {/* My Approvals Tab */}
        <TabsContent value="inbox" className="mt-3">
          {pendingApprovals.length === 0 ? (
            <Card><CardContent className="py-8 text-center">
              <CheckCircle className="w-10 h-10 text-green-500 mx-auto mb-3" />
              <p className="text-muted-foreground">No pending approvals</p>
            </CardContent></Card>
          ) : (
            <div className="space-y-2">
              {pendingApprovals.map((item, idx) => (
                <Card key={`${item.type}-${item.id}-${idx}`}>
                  <CardContent className="py-3 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      {item.type === 'signature' ? <PenTool className="w-5 h-5 text-blue-600" /> : <CheckCircle className="w-5 h-5 text-amber-600" />}
                      <div>
                        <div className="font-medium text-sm">{item.title || item.step_name}</div>
                        <div className="text-xs text-muted-foreground">
                          {item.member_name && `${item.member_name} · `}
                          {item.signer_role || item.step_config?.approver_role || ''}
                        </div>
                      </div>
                    </div>
                    <Button size="sm" className="bg-v2accent hover:bg-v2accent/90 text-white"
                      onClick={() => { setSigningItem(item); setSigDialogOpen(true); }}>
                      <PenTool className="w-3 h-3 mr-1" /> Sign & Approve
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        {/* Sent Requests Tab */}
        <TabsContent value="sent" className="mt-3">
          {requests.filter(r => ['pending', 'sent', 'viewed'].includes(r.status)).length === 0 ? (
            <Card><CardContent className="py-8 text-center">
              <Send className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
              <p className="text-muted-foreground">No pending requests</p>
              <Button size="sm" className="mt-3" onClick={() => setShowNewRequestPage(true)}>
                <Plus className="w-3 h-3 mr-1" /> Send New Request
              </Button>
            </CardContent></Card>
          ) : (
            requests.filter(r => ['pending', 'sent', 'viewed'].includes(r.status)).map(renderRequest)
          )}
        </TabsContent>

        {/* Completed Tab — Envelope View */}
        <TabsContent value="signed" className="mt-3">
          {requests.filter(r => r.status === 'signed').length === 0 ? (
            <Card><CardContent className="py-8 text-center">
              <CheckCircle className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
              <p className="text-muted-foreground">No completed signatures yet</p>
            </CardContent></Card>
          ) : (
            /* Group by title to show envelope view */
            (() => {
              const grouped: Record<string, any[]> = {};
              requests.filter(r => r.status === 'signed').forEach(r => {
                const key = r.title || `Request ${r.id}`;
                if (!grouped[key]) grouped[key] = [];
                grouped[key].push(r);
              });
              return Object.entries(grouped).map(([title, reqs]) => (
                <Card key={title} className="mb-3">
                  <CardContent className="py-4">
                    <EnvelopeViewer requestId={reqs[0].id} />
                  </CardContent>
                </Card>
              ));
            })()
          )}
        </TabsContent>

        {/* All Tab */}
        <TabsContent value="all" className="mt-3">
          {linkedRequestId && !loading && !requests.some((r) => r.id === linkedRequestId) && (
            <div className="mb-3 rounded-lg border p-3" data-testid="signature-linked-request">
              <EnvelopeViewer requestId={linkedRequestId} />
            </div>
          )}
          {requests.map(renderRequest)}
        </TabsContent>
      </Tabs>

      {/* New Request Dialog — removed, using full page instead */}

      {/* Signature Dialog for Approvals */}
      <Dialog open={sigDialogOpen} onOpenChange={setSigDialogOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>{signingItem?.title || signingItem?.step_name || 'Approve & Sign'}</DialogTitle>
          </DialogHeader>
          <SignaturePad
            title="Sign to Approve"
            message="By signing, you confirm your approval and authorization."
            onSign={handleApprovalSign}
            onCancel={() => { setSigDialogOpen(false); setSigningItem(null); }}
          />
        </DialogContent>
      </Dialog>
    </div>
  );
}
